import type { T } from './messages';

/**
 * What the console *says* — and nothing about what anything is.
 *
 * The module catalogue (spec §3.1) and the scope vocabulary (spec §3.3) used to
 * be copied into this file, alongside `apps/cli` and `apps/admin-api`. They have
 * been promoted to `packages/domain` (`modules.ts`, next to `capabilitiesFor`),
 * so the three surfaces now read one table and the screens import `MODULES`,
 * `SCOPES`, `scopeSpec`, `CONCIERGE_SCOPE_LIST` and — since the console gained
 * a server-side **Enable concierge** action — `PARTNER_PATH_MODULES` straight
 * from `@yourstore/domain`.
 *
 * The two sentences a lifecycle move is quoted from used to live here too, as
 * `STATUS_MEANING` and `STATUS_CONSEQUENCE`. They have moved again, one step
 * further, into `messages/en.ts` and `messages/es.ts` (`status.meaning.*`, and
 * since the redesign `stage.*`, which replaced `status.consequence.*` with the
 * same consequences in plain words): they are console copy — how a consequence is worded
 * to the person about to cause it — and console copy is exactly what Domain A
 * of docs/i18n-design.md §2 asks to be translatable. **The status token they are
 * keyed by is not**, which is why the two lookups below take a raw `status`
 * string and hand back a translated sentence.
 *
 * The shortest true path through the wizard used to be here too, as
 * `PARTNER_PATH_MODULES`. It has followed the module table into
 * `@yourstore/domain` for the reason written down beside it there: it stopped
 * being the set a screen pre-ticks the moment `admin-api`'s Enable action began
 * switching exactly those four on by itself.
 *
 * This file still decides nothing. Every verb belongs to the server, which stays
 * the authority on every refusal the console renders.
 */

/**
 * Lifecycle vocabulary (spec §5.7.1, §4.4) — what each status means, said
 * plainly.
 *
 * A `switch` rather than a `Record<string, key>` so the compiler checks every
 * branch against the catalog, and so a status the console has never heard of —
 * a newer server, mid-rollout — answers with an empty note instead of a key.
 */
export function statusMeaning(t: T, status: string): string {
  switch (status) {
    case 'onboarding':
      return t('status.meaning.onboarding');
    case 'importing':
      return t('status.meaning.importing');
    case 'live':
      return t('status.meaning.live');
    case 'paused':
      return t('status.meaning.paused');
    case 'offboarded':
      return t('status.meaning.offboarded');
    default:
      return '';
  }
}

/**
 * **The stages** — what an operator is shown instead of the status token
 * (docs/console-redesign-plan.md §2): Setting up, Moving their book in, Live,
 * and Paused and Offboarded off to the side. The token is still what is sent
 * and what the log records; this is only its name on screen.
 *
 * An unknown status (a newer server mid-rollout) is named by its token rather
 * than by nothing, for the same reason `reasonLabel` names an unknown health
 * reason by its token.
 */
export function stageName(t: T, status: string): string {
  switch (status) {
    case 'onboarding':
      return t('stage.name.onboarding');
    case 'importing':
      return t('stage.name.importing');
    case 'live':
      return t('stage.name.live');
    case 'paused':
      return t('stage.name.paused');
    case 'offboarded':
      return t('stage.name.offboarded');
    default:
      return status;
  }
}

/**
 * Everything the "Change stage" menu and its confirm say about one move.
 *
 * The plain-words successor to `status.consequence.*`: the old sentences spoke
 * the agreement's vocabulary (`authority`, `capabilities`, `reserve none`) to
 * an operator who needs to know what happens to *the business and whoever is
 * booking with it*. What they said is unchanged — `live` is still the cutover,
 * paused is still "partners route around it" — only the words are.
 *
 * `null` for a status the console has never heard of, so the menu leaves it
 * out rather than offering a move it cannot describe.
 */
export interface StageMove {
  label: string;
  meaning: string;
  title: string;
  consequence: string;
  confirm: string;
}

export function stageMove(t: T, status: string, name: string): StageMove | null {
  switch (status) {
    case 'onboarding':
      return {
        label: t('stage.option.onboarding'),
        meaning: t('stage.meaning.onboarding'),
        title: t('stage.confirm.title.onboarding', { name }),
        consequence: t('stage.consequence.onboarding', { name }),
        confirm: t('stage.confirm.onboarding'),
      };
    case 'importing':
      return {
        label: t('stage.option.importing'),
        meaning: t('stage.meaning.importing'),
        title: t('stage.confirm.title.importing', { name }),
        consequence: t('stage.consequence.importing', { name }),
        confirm: t('stage.confirm.importing'),
      };
    case 'live':
      return {
        label: t('stage.option.live'),
        meaning: t('stage.meaning.live'),
        title: t('stage.confirm.title.live', { name }),
        consequence: t('stage.consequence.live', { name }),
        confirm: t('stage.confirm.live'),
      };
    case 'paused':
      return {
        label: t('stage.option.paused'),
        meaning: t('stage.meaning.paused'),
        title: t('stage.confirm.title.paused', { name }),
        consequence: t('stage.consequence.paused', { name }),
        confirm: t('stage.confirm.paused'),
      };
    case 'offboarded':
      return {
        label: t('stage.option.offboarded'),
        meaning: t('stage.meaning.offboarded'),
        title: t('stage.confirm.title.offboarded', { name }),
        consequence: t('stage.consequence.offboarded', { name }),
        confirm: t('stage.confirm.offboarded'),
      };
    default:
      return null;
  }
}
